import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Enhanced WebSocket Hook for Real-time Updates with Performance Optimizations
 * Supports portfolio, investment pool, alerts, and public analytics subscriptions
 * Features: Message batching, compression, rate limiting, performance monitoring
 */
export const useEnhancedWebSocket = (options = {}) => {
  const {
    autoConnect = true,
    reconnectAttempts = 5,
    reconnectInterval = 3000,
    heartbeatInterval = 30000,
    enableReconnection = true,
    enableBatching = true,
    batchSize = 10,
    batchTimeout = 100,
    enableCompression = false,
    maxLatencyMeasurements = 50,
    rateLimitPerMinute = 60
  } = options;

  const { tokens } = useAuth();
  const [connectionState, setConnectionState] = useState('CLOSED');
  const [lastMessage, setLastMessage] = useState(null);
  const [subscriptions, setSubscriptions] = useState(new Set());
  const [messageStats, setMessageStats] = useState({
    sent: 0,
    received: 0,
    errors: 0,
    avgLatency: 0,
    batchesSent: 0,
    messagesQueued: 0
  });
  
  const ws = useRef(null);
  const reconnectCount = useRef(0);
  const reconnectTimeoutId = useRef(null);
  const heartbeatTimeoutId = useRef(null);
  const isConnecting = useRef(false);
  
  // Performance optimization refs
  const messageBuffer = useRef([]);
  const batchTimeoutRef = useRef(null);
  const latencyMeasurements = useRef([]);
  const rateLimitCounter = useRef({ count: 0, resetTime: Date.now() });
  const messageQueue = useRef([]);
  
  // Event handlers
  const [eventHandlers, setEventHandlers] = useState({
    onPortfolioUpdate: null,
    onPoolUpdate: null,
    onAlertNotification: null,
    onPublicAnalyticsUpdate: null,
    onPriceUpdate: null,
    onConnectionChange: null,
    onError: null
  });

  const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'wss://safepumpbackend.urion.live/ws';

  /**
   * Set event handlers
   */
  const setEventHandler = useCallback((eventType, handler) => {
    setEventHandlers(prev => ({
      ...prev,
      [eventType]: handler
    }));
  }, []);

  // Performance monitoring functions
  const updateMessageStats = useCallback((type, latency = null) => {
    setMessageStats(prev => {
      const updated = { ...prev };
      
      if (type === 'sent') updated.sent++;
      if (type === 'received') updated.received++;
      if (type === 'error') updated.errors++;
      if (type === 'batch_sent') updated.batchesSent++;
      if (type === 'queued') updated.messagesQueued++;
      
      if (latency !== null) {
        latencyMeasurements.current.push(latency);
        if (latencyMeasurements.current.length > maxLatencyMeasurements) {
          latencyMeasurements.current.shift();
        }
        
        const avgLatency = latencyMeasurements.current.reduce((a, b) => a + b, 0) / 
                         latencyMeasurements.current.length;
        updated.avgLatency = Math.round(avgLatency);
      }
      
      return updated;
    });
  }, [maxLatencyMeasurements]);

  // Rate limiting check
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceReset = now - rateLimitCounter.current.resetTime;
    
    // Reset counter every minute
    if (timeSinceReset >= 60000) {
      rateLimitCounter.current = { count: 0, resetTime: now };
    }
    
    if (rateLimitCounter.current.count >= rateLimitPerMinute) {
      return false;
    }
    
    rateLimitCounter.current.count++;
    return true;
  }, [rateLimitPerMinute]);

  // Process batched messages
  const processBatchedMessages = useCallback(() => {
    if (messageBuffer.current.length === 0) return;
    
    const messages = messageBuffer.current.splice(0);
    
    if (enableBatching && messages.length > 1) {
      // Send as batch
      const batchMessage = {
        type: 'batch',
        messages: messages,
        timestamp: Date.now()
      };
      
      if (ws.current?.readyState === WebSocket.OPEN) {
        const messageStr = enableCompression ? 
          JSON.stringify(batchMessage) : // Could add compression here
          JSON.stringify(batchMessage);
          
        ws.current.send(messageStr);
        updateMessageStats('batch_sent');
      }
    } else {
      // Send individual messages
      messages.forEach(message => {
        if (ws.current?.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify(message));
          updateMessageStats('sent');
        }
      });
    }
  }, [enableBatching, enableCompression, updateMessageStats]);

  /**
   * Handle incoming WebSocket messages with performance tracking
   */
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      const receiveTime = Date.now();
      
      // Calculate latency for ping/pong
      if (message.type === 'pong' && message.timestamp) {
        const latency = receiveTime - message.timestamp;
        updateMessageStats('received', latency);
      } else {
        updateMessageStats('received');
      }
      
      // Handle batched messages
      if (message.type === 'batch' && message.messages) {
        message.messages.forEach(batchedMessage => {
          setLastMessage(batchedMessage);
          handleSingleMessage(batchedMessage);
        });
      } else {
        setLastMessage(message);
        handleSingleMessage(message);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
      updateMessageStats('error');
      eventHandlers.onError?.('Failed to parse message');
    }
  }, [updateMessageStats, eventHandlers]);

  /**
   * Handle individual message processing
   */
  const handleSingleMessage = useCallback((message) => {
    switch (message.type) {
        case 'connection_established':
          console.log('🔌 WebSocket connection established:', message.connectionId);
          break;

        case 'authenticated':
          console.log('✅ WebSocket authenticated for user:', message.userId);
          setConnectionState('AUTHENTICATED');
          break;

        case 'portfolio_update':
          console.log('📊 Portfolio update received:', message.data);
          eventHandlers.onPortfolioUpdate?.(message.data);
          break;

        case 'pool_update':
          console.log('🏦 Pool update received:', message.data);
          eventHandlers.onPoolUpdate?.(message.poolId, message.data);
          break;

        case 'alert_notification':
          console.log('🔔 Alert notification received:', message.data);
          eventHandlers.onAlertNotification?.(message.data);
          break;

        case 'public_analytics_update':
          console.log('📈 Public analytics update received');
          eventHandlers.onPublicAnalyticsUpdate?.(message.data);
          break;

        case 'price_update':
          console.log('💰 Price update received:', message.tokenMint);
          eventHandlers.onPriceUpdate?.(message.tokenMint, message.data);
          break;

        case 'portfolio_subscription_confirmed':
        case 'pool_subscription_confirmed':
        case 'alerts_subscription_confirmed':
        case 'public_analytics_subscription_confirmed':
        case 'subscription_confirmed':
          console.log('✅ Subscription confirmed:', message.message);
          break;

        case 'error':
        case 'auth_error':
          console.error('❌ WebSocket error:', message.message);
          eventHandlers.onError?.(message.message);
          break;

        case 'pong':
          // Heartbeat response - latency already calculated above
          break;

        default:
          console.log('📨 Unknown WebSocket message:', message);
    }
  }, [eventHandlers]);

  /**
   * Start heartbeat to keep connection alive with latency measurement
   */
  const startHeartbeat = useCallback(() => {
    if (heartbeatTimeoutId.current) {
      clearTimeout(heartbeatTimeoutId.current);
    }

    heartbeatTimeoutId.current = setTimeout(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        const pingTime = Date.now();
        ws.current.send(JSON.stringify({ 
          type: 'ping', 
          timestamp: pingTime 
        }));
        updateMessageStats('sent');
        startHeartbeat(); // Schedule next heartbeat
      }
    }, heartbeatInterval);
  }, [heartbeatInterval, updateMessageStats]);

  /**
   * Stop heartbeat and batch processing
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimeoutId.current) {
      clearTimeout(heartbeatTimeoutId.current);
      heartbeatTimeoutId.current = null;
    }
    
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
      batchTimeoutRef.current = null;
    }
    
    // Process any remaining batched messages
    processBatchedMessages();
  }, [processBatchedMessages]);

  /**
   * Schedule reconnection attempt
   */
  const scheduleReconnect = useCallback(() => {
    if (!enableReconnection || reconnectCount.current >= reconnectAttempts) {
      console.log('Max reconnection attempts reached or reconnection disabled');
      return;
    }

    const delay = reconnectInterval * Math.pow(1.5, reconnectCount.current);
    console.log(`Scheduling reconnection attempt ${reconnectCount.current + 1} in ${delay}ms`);
    
    reconnectTimeoutId.current = setTimeout(() => {
      reconnectCount.current++;
      connect();
    }, delay);
  }, [enableReconnection, reconnectAttempts, reconnectInterval]);

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(() => {
    if (isConnecting.current || (ws.current && ws.current.readyState === WebSocket.OPEN)) {
      return;
    }

    isConnecting.current = true;
    setConnectionState('CONNECTING');

    try {
      ws.current = new WebSocket(WS_BASE_URL);

      ws.current.onopen = (event) => {
        console.log('🔌 Enhanced WebSocket connected');
        setConnectionState('OPEN');
        isConnecting.current = false;
        reconnectCount.current = 0;
        
        startHeartbeat();
        eventHandlers.onConnectionChange?.('OPEN');
        
        // Send queued messages
        if (messageQueue.current.length > 0) {
          const queuedMessages = messageQueue.current.splice(0);
          queuedMessages.forEach(msg => {
            ws.current.send(JSON.stringify(msg));
            updateMessageStats('sent');
          });
        }
        
        // Authenticate if we have tokens
        if (tokens?.access_token) {
          authenticate();
        }
      };

      ws.current.onmessage = handleMessage;

      ws.current.onclose = (event) => {
        console.log('🔌 Enhanced WebSocket disconnected:', event.code, event.reason);
        setConnectionState('CLOSED');
        isConnecting.current = false;
        stopHeartbeat();
        
        eventHandlers.onConnectionChange?.('CLOSED');
        
        if (enableReconnection && event.code !== 1000) { // Don't reconnect on normal closure
          scheduleReconnect();
        }
      };

      ws.current.onerror = (error) => {
        console.error('🔌 Enhanced WebSocket error:', error);
        setConnectionState('ERROR');
        isConnecting.current = false;
        eventHandlers.onError?.('Connection error');
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionState('ERROR');
      isConnecting.current = false;
      eventHandlers.onError?.('Failed to create connection');
    }
  }, [WS_BASE_URL, tokens, handleMessage, startHeartbeat, stopHeartbeat, scheduleReconnect, enableReconnection, eventHandlers]);

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutId.current) {
      clearTimeout(reconnectTimeoutId.current);
      reconnectTimeoutId.current = null;
    }
    
    stopHeartbeat();
    
    if (ws.current) {
      ws.current.close(1000, 'Manual disconnect');
      ws.current = null;
    }
    
    setConnectionState('CLOSED');
    isConnecting.current = false;
    setSubscriptions(new Set());
  }, [stopHeartbeat]);

  /**
   * Authenticate with the WebSocket server
   */
  const authenticate = useCallback(() => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN && tokens?.access_token) {
      ws.current.send(JSON.stringify({
        type: 'authenticate',
        token: tokens.access_token
      }));
    }
  }, [tokens]);

  /**
   * Send message to WebSocket server with batching and rate limiting
   */
  const sendMessage = useCallback((message) => {
    // Check rate limit
    if (!checkRateLimit()) {
      console.warn('Rate limit exceeded, queueing message');
      messageQueue.current.push(message);
      updateMessageStats('queued');
      return false;
    }
    
    const messageObj = typeof message === 'string' ? 
      { type: 'message', data: message } : 
      { ...message, timestamp: Date.now() };
    
    if (ws.current?.readyState === WebSocket.OPEN) {
      if (enableBatching && messageObj.type !== 'ping' && messageObj.type !== 'pong') {
        // Add to batch
        messageBuffer.current.push(messageObj);
        
        // Clear existing timeout
        if (batchTimeoutRef.current) {
          clearTimeout(batchTimeoutRef.current);
        }
        
        // Process batch if full or set timeout
        if (messageBuffer.current.length >= batchSize) {
          processBatchedMessages();
        } else {
          batchTimeoutRef.current = setTimeout(processBatchedMessages, batchTimeout);
        }
      } else {
        // Send immediately
        ws.current.send(JSON.stringify(messageObj));
        updateMessageStats('sent');
      }
      return true;
    } else {
      console.warn('WebSocket is not connected. Message queued:', message);
      messageQueue.current.push(messageObj);
      updateMessageStats('queued');
      return false;
    }
  }, [checkRateLimit, enableBatching, batchSize, batchTimeout, processBatchedMessages, updateMessageStats]);

  /**
   * Subscribe to portfolio updates
   */
  const subscribeToPortfolio = useCallback(() => {
    if (sendMessage({ type: 'subscribe_portfolio' })) {
      setSubscriptions(prev => new Set([...prev, 'portfolio']));
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Unsubscribe from portfolio updates
   */
  const unsubscribeFromPortfolio = useCallback(() => {
    if (sendMessage({ type: 'unsubscribe_portfolio' })) {
      setSubscriptions(prev => {
        const newSubs = new Set(prev);
        newSubs.delete('portfolio');
        return newSubs;
      });
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Subscribe to investment pool updates
   */
  const subscribeToPool = useCallback((poolId) => {
    if (sendMessage({ type: 'subscribe_pool', poolId })) {
      setSubscriptions(prev => new Set([...prev, `pool:${poolId}`]));
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Unsubscribe from investment pool updates
   */
  const unsubscribeFromPool = useCallback((poolId) => {
    if (sendMessage({ type: 'unsubscribe_pool', poolId })) {
      setSubscriptions(prev => {
        const newSubs = new Set(prev);
        newSubs.delete(`pool:${poolId}`);
        return newSubs;
      });
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Subscribe to alert notifications
   */
  const subscribeToAlerts = useCallback(() => {
    if (sendMessage({ type: 'subscribe_alerts' })) {
      setSubscriptions(prev => new Set([...prev, 'alerts']));
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Unsubscribe from alert notifications
   */
  const unsubscribeFromAlerts = useCallback(() => {
    if (sendMessage({ type: 'unsubscribe_alerts' })) {
      setSubscriptions(prev => {
        const newSubs = new Set(prev);
        newSubs.delete('alerts');
        return newSubs;
      });
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Subscribe to public analytics updates
   */
  const subscribeToPublicAnalytics = useCallback(() => {
    if (sendMessage({ type: 'subscribe_public_analytics' })) {
      setSubscriptions(prev => new Set([...prev, 'public_analytics']));
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Unsubscribe from public analytics updates
   */
  const unsubscribeFromPublicAnalytics = useCallback(() => {
    if (sendMessage({ type: 'unsubscribe_public_analytics' })) {
      setSubscriptions(prev => {
        const newSubs = new Set(prev);
        newSubs.delete('public_analytics');
        return newSubs;
      });
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Subscribe to price updates for a token
   */
  const subscribeToPriceUpdates = useCallback((tokenMint) => {
    if (sendMessage({ type: 'subscribe_price', tokenMint })) {
      setSubscriptions(prev => new Set([...prev, `price:${tokenMint}`]));
      return true;
    }
    return false;
  }, [sendMessage]);

  /**
   * Unsubscribe from price updates for a token
   */
  const unsubscribeFromPriceUpdates = useCallback((tokenMint) => {
    if (sendMessage({ type: 'unsubscribe_price', tokenMint })) {
      setSubscriptions(prev => {
        const newSubs = new Set(prev);
        newSubs.delete(`price:${tokenMint}`);
        return newSubs;
      });
      return true;
    }
    return false;
  }, [sendMessage]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Re-authenticate when tokens change
  useEffect(() => {
    if (connectionState === 'OPEN' && tokens?.access_token) {
      authenticate();
    }
  }, [tokens, connectionState, authenticate]);

  return {
    // Connection state
    connectionState,
    isConnected: connectionState === 'AUTHENTICATED',
    isConnecting: connectionState === 'CONNECTING',
    lastMessage,
    subscriptions: Array.from(subscriptions),
    
    // Connection methods
    connect,
    disconnect,
    sendMessage,
    
    // Subscription methods
    subscribeToPortfolio,
    unsubscribeFromPortfolio,
    subscribeToPool,
    unsubscribeFromPool,
    subscribeToAlerts,
    unsubscribeFromAlerts,
    subscribeToPublicAnalytics,
    unsubscribeFromPublicAnalytics,
    subscribeToPriceUpdates,
    unsubscribeFromPriceUpdates,
    
    // Event handler management
    setEventHandler,
    
    // Performance monitoring
    messageStats,
    getPerformanceMetrics: () => ({
      ...messageStats,
      connectionUptime: connectionState === 'AUTHENTICATED' ? 
        Date.now() - (reconnectCount.current === 0 ? Date.now() : 0) : 0,
      reconnectAttempts: reconnectCount.current,
      queuedMessages: messageQueue.current.length,
      batchedMessages: messageBuffer.current.length,
      activeSubscriptions: subscriptions.size,
      latencyHistory: [...latencyMeasurements.current],
      avgLatency: messageStats.avgLatency
    }),
    
    // Utility methods
    getConnectionState: () => ws.current ? ws.current.readyState : WebSocket.CLOSED,
    reconnectCount: reconnectCount.current,
    clearMessageQueue: () => {
      messageQueue.current = [];
      messageBuffer.current = [];
    }
  };
};

export default useEnhancedWebSocket;